import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import UserManagementHelp from '../../../components/Shared/HelpFIles/UserManagementHelp.tsx';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import {
  createUserFormDataAtom,
  profileObjAtom,
  userInformationAtom,
} from '../../../Services/_states/index.js';
import { ROUTES } from '../../../constants/routes';
import { buildBreadcrumbs, createBreadcrumbNavigateHandler } from '../../../config/navigation/breadcrumbs';
import { Button, ListPageHeader } from '../../../components/ui';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider.jsx';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';
import UserChangePasswordDialog from '../components/UserChangePasswordDialog.jsx';
import UserDeleteDialog from '../components/UserDeleteDialog.jsx';
import UserStatusDialog from '../components/UserStatusDialog.jsx';
import UserFormDrawer from '../components/UserFormDrawer.jsx';
import UserListTable from '../components/UserListTable.jsx';
import UserOrganisationDetailDialog from '../components/UserOrganisationDetailDialog.jsx';
import UserTableFilters from '../components/UserTableFilters.jsx';
import useUserList from '../hooks/useUserList.js';
import useUserManagementPermissions from '../hooks/useUserManagementPermissions.js';
import useUserProfileFilterOptions from '../hooks/useUserProfileFilterOptions.js';
import { userService } from '../services/userService.js';
import { USER_STATUS_MESSAGES } from '../config/userStatusCopy';
import { isSameUser } from '../utils/userAccessUtils';

export default function UserManagementPage() {
  return (
    <FeatureThemeProvider>
      <UserManagementPageContent />
    </FeatureThemeProvider>
  );
}

function UserManagementPageContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { semantic } = useFeatureTheme();
  const { showSnackBar } = useSnackBar();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const { canCreate } = useUserManagementPermissions();
  const createUserFormData = useRecoilValue(createUserFormDataAtom);
  const setCreateUserFormData = useSetRecoilState(createUserFormDataAtom);
  const resetCreateUserFormData = useResetRecoilState(createUserFormDataAtom);
  const setProfileObject = useSetRecoilState(profileObjAtom);

  const {
    users,
    count,
    filtersObj,
    isLoading,
    handleSearch,
    handleOrganisationChange,
    handleProfileChange,
    handleStatusChange,
    applyFilters,
    handlePageChange,
    handlePageSizeChange,
    deleteUser,
    unlockUser,
    setUserStatus,
    changeUserPassword,
    refreshList,
  } = useUserList();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusActivating, setStatusActivating] = useState(false);
  const [isStatusSubmitting, setIsStatusSubmitting] = useState(false);
  const [orgDetail, setOrgDetail] = useState(null);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [profileFilterAlert, setProfileFilterAlert] = useState('');
  const deepLinkAppliedRef = useRef('');

  const showSuperuserColumns = Boolean(loggedInUser?.is_superuser);
  const profileOptionsOrgId = showSuperuserColumns
    ? filtersObj?.organisation_id || ''
    : loggedInUser?.profile?.organisation?.organisation_id || '';
  const { profileOptions, isLoading: isProfileOptionsLoading } = useUserProfileFilterOptions(
    profileOptionsOrgId
  );
  const page = Math.max(0, (filtersObj?.page || 1) - 1);
  const pageSize = filtersObj?.limit || 20;

  const breadcrumbsData = useMemo(
    () => buildBreadcrumbs([
      { label: 'Management', href: ROUTES.MANAGEMENT.USER_MANAGEMENT },
      { label: 'User Management' },
    ]),
    []
  );

  const openCreateForm = useCallback(() => {
    resetCreateUserFormData();
    setIsEditMode(false);
    setIsFormOpen(true);
  }, [resetCreateUserFormData]);

  const openEditForm = useCallback(
    (user) => {
      if (!user) {
        showSnackBar('User details is missing', 'info');
        return;
      }
      setCreateUserFormData({
        ...user,
        organisation: user?.profile?.organisation ?? null,
        profile: user?.profile ?? null,
      });
      setIsEditMode(true);
      setIsFormOpen(true);
    },
    [setCreateUserFormData, showSnackBar]
  );

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setIsEditMode(false);
    if (searchParams.get('drawer')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('drawer');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const drawer = searchParams.get('drawer');
    if (drawer === 'create' && canCreate) {
      openCreateForm();
    }
  }, [canCreate, openCreateForm, searchParams]);

  const clearProfileIdParam = useCallback(() => {
    if (!searchParams.get('profileId')) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('profileId');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleProfileFilterChange = useCallback(
    (profile) => {
      handleProfileChange(profile);
      deepLinkAppliedRef.current = profile?.id ? String(profile.id) : '';

      if (profile?.id) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('profileId', String(profile.id));
        setSearchParams(nextParams, { replace: true });
        setProfileFilterAlert(`Showing users assigned to ${profile.name}`);
        return;
      }

      clearProfileIdParam();
      setProfileFilterAlert('');
    },
    [clearProfileIdParam, handleProfileChange, searchParams, setSearchParams]
  );

  const handleOrganisationFilterChange = useCallback(
    (organisation) => {
      handleOrganisationChange(organisation);
      deepLinkAppliedRef.current = '';
      clearProfileIdParam();
      setProfileFilterAlert('');
    },
    [clearProfileIdParam, handleOrganisationChange]
  );

  useEffect(() => {
    const profileIdParam = searchParams.get('profileId');
    if (!profileIdParam) {
      deepLinkAppliedRef.current = '';
      return undefined;
    }

    if (deepLinkAppliedRef.current === profileIdParam) {
      return undefined;
    }

    if (
      filtersObj.profile_id === profileIdParam
      && filtersObj.selectedProfile
    ) {
      deepLinkAppliedRef.current = profileIdParam;
      return undefined;
    }

    let cancelled = false;

    const applyDeepLink = async () => {
      const organisationScope = showSuperuserColumns
        ? ''
        : loggedInUser?.profile?.organisation?.organisation_id || '';
      const profile = await userService.findProfileById(profileIdParam, {
        organisationId: organisationScope,
      });

      if (cancelled) {
        return;
      }

      if (!profile) {
        showSnackBar('Could not apply profile filter from link.', 'warning');
        clearProfileIdParam();
        return;
      }

      const partialFilters = {
        selectedProfile: profile,
        profile_id: String(profile.id),
      };

      if (showSuperuserColumns && profile.organisation) {
        partialFilters.selectedOrganisation = profile.organisation;
        partialFilters.organisation_id =
          profile.organisation.organisation_id
          || profile.organisation.id
          || '';
      }

      applyFilters(partialFilters);
      setProfileFilterAlert(`Showing users assigned to ${profile.name}`);
      deepLinkAppliedRef.current = profileIdParam;
    };

    applyDeepLink();

    return () => {
      cancelled = true;
    };
  }, [
    applyFilters,
    clearProfileIdParam,
    filtersObj.profile_id,
    filtersObj.selectedProfile,
    loggedInUser,
    searchParams,
    showSnackBar,
    showSuperuserColumns,
  ]);

  const handleFormSuccess = useCallback(() => {
    refreshList();
  }, [refreshList]);

  const handleChangePassword = useCallback(
    (user) => {
      setCreateUserFormData(user);
      setPasswordDialogOpen(true);
    },
    [setCreateUserFormData]
  );

  const handlePasswordDialogClose = useCallback(() => {
    setPasswordDialogOpen(false);
    resetCreateUserFormData();
  }, [resetCreateUserFormData]);

  const handlePasswordSubmit = useCallback(
    async (password) => {
      const userId = createUserFormData?.id;
      if (!userId) {
        return;
      }
      await changeUserPassword(userId, password);
      resetCreateUserFormData();
    },
    [changeUserPassword, createUserFormData?.id, resetCreateUserFormData]
  );

  const handleDeactivateRequest = useCallback(
    (user) => {
      if (isSameUser(loggedInUser, user)) {
        showSnackBar(USER_STATUS_MESSAGES.cannotDeactivateSelf, 'info');
        return;
      }
      setStatusActivating(false);
      setStatusTarget(user);
    },
    [loggedInUser, showSnackBar]
  );

  const handleActivateRequest = useCallback((user) => {
    setStatusActivating(true);
    setStatusTarget(user);
  }, []);

  const handleConfirmStatusChange = useCallback(async () => {
    if (!statusTarget) {
      return;
    }
    setIsStatusSubmitting(true);
    try {
      const success = await setUserStatus(statusTarget, statusActivating);
      if (success) {
        setStatusTarget(null);
      }
    } finally {
      setIsStatusSubmitting(false);
    }
  }, [setUserStatus, statusActivating, statusTarget]);

  const handleDeleteRequest = useCallback(
    (user, index) => {
      if (isSameUser(loggedInUser, user)) {
        showSnackBar('You cannot delete your own account.', 'info');
        return;
      }
      setDeleteTarget(user);
      setDeleteIndex(index);
    },
    [loggedInUser, showSnackBar]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget || deleteIndex === null) {
      return;
    }
    setIsDeleting(true);
    try {
      const success = await deleteUser(deleteTarget, deleteIndex);
      if (success) {
        setDeleteTarget(null);
        setDeleteIndex(null);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [deleteIndex, deleteTarget, deleteUser]);

  const handleProfileClick = useCallback(
    (profile) => {
      setProfileObject(profile);
      navigate('/r/management/profile-permissions/view');
    },
    [navigate, setProfileObject]
  );

  const handleOrganisationClick = useCallback((organisationName) => {
    setOrgDetail(organisationName);
    setOrgDialogOpen(true);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        minHeight: 0,
        px: { xs: 2, md: 3 },
        py: 2.5,
        bgcolor: semantic.surfaces.surfaceSubtle,
      }}
    >
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={createBreadcrumbNavigateHandler(navigate)}
      />

      <ListPageHeader
        title="User Management"
        subtitle="Manage users, profiles, and access for your organisation."
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {canCreate ? (
              <Button variant="primary" icon={<AddIcon />} onClick={openCreateForm}>
                Add User
              </Button>
            ) : null}
            <UserManagementHelp />
          </Box>
        }
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        {profileFilterAlert ? (
          <Alert
            severity="info"
            onClose={() => setProfileFilterAlert('')}
            sx={{ mb: 0 }}
          >
            {profileFilterAlert}
          </Alert>
        ) : null}

        <UserTableFilters
          search={filtersObj?.search || ''}
          onSearchChange={handleSearch}
          profileFilter={filtersObj?.selectedProfile || null}
          onProfileChange={handleProfileFilterChange}
          profileOptions={profileOptions}
          profileOptionsLoading={isProfileOptionsLoading}
          statusFilter={filtersObj?.selectedStatus || null}
          onStatusChange={handleStatusChange}
          organisationFilter={filtersObj?.selectedOrganisation || null}
          onOrganisationChange={handleOrganisationFilterChange}
          showOrgFilter={showSuperuserColumns}
          disabled={isLoading}
        />

        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
          <UserListTable
            users={users}
            loading={isLoading}
            page={page}
            pageSize={pageSize}
            totalCount={count}
            showSuperuserColumns={showSuperuserColumns}
            onEdit={openEditForm}
            onChangePassword={handleChangePassword}
            onUnlock={unlockUser}
            onDeactivate={handleDeactivateRequest}
            onActivate={handleActivateRequest}
            onDelete={handleDeleteRequest}
            onOrganisationClick={handleOrganisationClick}
            onProfileClick={handleProfileClick}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </Box>
      </Box>

      <UserFormDrawer
        open={isFormOpen}
        isEditMode={isEditMode}
        onClose={closeForm}
        onSuccess={handleFormSuccess}
      />

      <UserChangePasswordDialog
        open={passwordDialogOpen}
        onClose={handlePasswordDialogClose}
        onChangePassword={handlePasswordSubmit}
      />

      <UserDeleteDialog
        open={Boolean(deleteTarget)}
        user={deleteTarget}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteIndex(null);
        }}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />

      <UserStatusDialog
        open={Boolean(statusTarget)}
        user={statusTarget}
        activating={statusActivating}
        onClose={() => setStatusTarget(null)}
        onConfirm={handleConfirmStatusChange}
        isSubmitting={isStatusSubmitting}
      />

      <UserOrganisationDetailDialog
        open={orgDialogOpen}
        onClose={() => {
          setOrgDialogOpen(false);
          setOrgDetail(null);
        }}
        organisation={orgDetail}
      />
    </Box>
  );
}
